import styles from "../../../../components/TaskCard/TaskButton.module.scss";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { JoinChannelTaskDto } from "./JoinChannelTaskPage";
import { JoinChannelTaskCardType, TaskCard } from "../../../../components/TaskCard/TaskCard";
import { useDispatch, useSelector } from "../../../../hooks";
import { checkJoinChannelsTasks, CheckJoinChannelTaskResult } from "../../../../redux/tasks/check-join-channel.handler";
import { getKeyByValue } from "../../../../utils/enums";
import { setUserBalanceAndLog } from "../../../../redux/user/set-user-balance-and-log.handler";
import { Highlightable } from "../../../../components/Highlight/Highlightable";
import { ActionPromptType, usePrompts } from "../../../../hooks/prompts";
import { PendingBlock } from "../../../../components/TaskCard/PendingInfo/PendingInfo";
import { reachYMGoal, YMGoals } from "../../../../components/App/YandexMetrika";
import { viewJoinChannelsTasks } from "../../../../redux/tasks/view-join-channel.handler";
import { openPlatformLink } from "../../../../utils/platform-links";

enum TaskState {
    Perform = 'Подписаться',
    Check = 'Проверить',
    Completed = 'Выполнено',
    Repeat = 'Возобновить',
    Archive = 'Архив'
}

interface IJoinChannelItemProps {
    task: JoinChannelTaskDto;
    index: number;
    type: JoinChannelTaskCardType;
}

export const JoinChannelItem: FC<IJoinChannelItemProps> = ({ task, index, type }) => {
    const getState = useCallback(() => {
        if (type === 'new') {
            if (task.subscribed)
                return TaskState.Completed;
            if (task.linkOpened)
                return TaskState.Check;
            return TaskState.Perform;
        }
        else if (type === 'active')
            return TaskState.Completed;
        else if (type === 'archive')
            return TaskState.Archive;
        else
            return TaskState.Repeat;

    }, [task, type]);

    const [stage, setStage] = useState<TaskState>(getState())
    const dispatch = useDispatch();
    const { removePrompts } = usePrompts();
    const checkInFlight = useRef(false);

    const { loading, joinChannelTasks } = useSelector(store => store.tasks);

    useEffect(() => {
        setStage(getState());
    }, [task, getState]);

    const handler = async () => {
        if (stage === TaskState.Perform || stage === TaskState.Repeat) {
            dispatch(viewJoinChannelsTasks(task.taskId));
            openPlatformLink(task.link);
            setStage(TaskState.Check);
            return;
        }

        if (stage === TaskState.Check) {
            if (checkInFlight.current)
                return;

            checkInFlight.current = true;
            try {
                const resultAction = await dispatch(checkJoinChannelsTasks(task.taskId));

                if (checkJoinChannelsTasks.fulfilled.match(resultAction)) {
                    reachYMGoal(YMGoals.JoinChannelTask);

                    const { balance } = resultAction.payload;
                    if (balance !== undefined)
                        dispatch(setUserBalanceAndLog(balance));

                    const count = (joinChannelTasks?.activeTasks ?? [])
                        .reduce((acc, item) => {
                            if (!item.subscribed)
                                return ++acc;

                            return acc;
                        }, 0);

                    if (count === 1)
                        removePrompts(ActionPromptType.AVAILABLE_SUBSCRIPTION_CHANNELS);

                    if (resultAction.payload.result === CheckJoinChannelTaskResult.NotSubscribed)
                        setStage(TaskState.Perform);
                }
            }
            finally {
                checkInFlight.current = false;
            }

            return;
        }
    }

    const getButton = () => {
        if (stage === TaskState.Completed || stage === TaskState.Archive)
            return;
        
        return (
            <Highlightable promptType={ActionPromptType.COMPLETE_SUBSCRIPTION_TASK_FIRST_TIME} highlightClass={ styles.highlight } >
                <button
                    className={`${styles.button} ${styles[getKeyByValue(TaskState, stage)]}`}
                    onClick={ handler }
                >
                    { loading ? '' : stage }
                </button>
            </Highlightable>
        );
    }

    const getControlItem = () => {
        if (task.result === CheckJoinChannelTaskResult.PendingCheck)
            return <PendingBlock pendingDate={ task.pendingDate ?? 0 } taskReward={ task.reward.balance } />;

        return getButton();
    }
    
    return (
        <TaskCard
            key={ `joinChannelTask${task.link}` }
            task={ task }
            index={ index }
            performButton={ getControlItem() }
            type={ type }
            avatarFallbackToTitle
        />
    );
}